import React from 'react';
import { Helmet } from 'react-helmet-async';
import { seoConfig } from '../utils/seoConfig';

/**
 * SEO Head组件 - 统一管理页面的SEO信息
 * @param {string} pageKey - 页面标识符，对应seoConfig中的键
 * @param {object} customSeo - 自定义SEO数据，会覆盖默认配置
 */
const SEOHead = ({ pageKey = 'home', customSeo = {} }) => {
  const metaData = seoConfig.generateMetaData(pageKey, customSeo);
  const jsonLd = seoConfig.generateJsonLd(pageKey, customSeo);

  return (
    <Helmet>
      {/* 基本meta标签 */}
      <title>{metaData.title}</title>
      <meta name="description" content={metaData.description} />
      <meta name="keywords" content={metaData.keywords} />
      <meta name="author" content={metaData.author} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content="index,follow" />
      <meta name="language" content="English" />
      
      {/* 地理位置标签 - 针对新加坡用户 */}
      <meta name="geo.region" content="SG" />
      <meta name="geo.placename" content="Singapore" />
      <meta name="geo.position" content="1.3521;103.8198" />
      <meta name="ICBM" content="1.3521, 103.8198" />
      
      {/* Google特定的meta标签 */}
      <meta name="googlebot" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      <meta name="bingbot" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      <meta name="google-site-verification" content="" />
      
      {/* 页面分类和主题 */}
      <meta name="subject" content="Chinese Language Learning" />
      <meta name="topic" content="Mandarin Education Singapore" />
      <meta name="coverage" content="Worldwide" />
      <meta name="distribution" content="Global" />
      <meta name="target" content="all" />
      <meta name="audience" content="all" />
      <meta name="rating" content="general" />
      
      {/* Open Graph标签 - 社交媒体分享 */}
      <meta property="og:type" content={metaData.type} />
      <meta property="og:title" content={metaData.ogTitle} />
      <meta property="og:description" content={metaData.ogDescription} />
      <meta property="og:image" content={metaData.ogImage} />
      <meta property="og:image:secure_url" content={metaData.ogImage} />
      <meta property="og:image:width" content={metaData.imageWidth} />
      <meta property="og:image:height" content={metaData.imageHeight} />
      <meta property="og:image:alt" content={metaData.ogTitle} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:url" content={metaData.ogUrl} />
      <meta property="og:site_name" content={metaData.siteName} />
      <meta property="og:locale" content={metaData.locale} />
      
      {/* Twitter Card标签 - 使用专门的Twitter图片 */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaData.ogTitle} />
      <meta name="twitter:description" content={metaData.ogDescription} />
      <meta name="twitter:image" content={metaData.twitterImage || metaData.ogImage} />
      <meta name="twitter:image:alt" content={metaData.ogTitle} />
      
      {/* Instagram优化 - 添加正方形图片支持 */}
      {metaData.instagramImage && (
        <>
          <meta property="og:image" content={metaData.instagramImage} />
          <meta property="og:image:width" content={metaData.instagramImageWidth} />
          <meta property="og:image:height" content={metaData.instagramImageHeight} />
        </>
      )}
      
      {/* 规范链接 */}
      <link rel="canonical" href={metaData.canonicalUrl} />
      
      {/* 多语言支持 */}
      <link rel="alternate" hrefLang="en" href={metaData.canonicalUrl} />
      <link rel="alternate" hrefLang="en-sg" href={metaData.canonicalUrl} />
      <link rel="alternate" hrefLang="en-my" href={metaData.canonicalUrl} />
      <link rel="alternate" hrefLang="en-us" href={metaData.canonicalUrl} />
      <link rel="alternate" hrefLang="en-au" href={metaData.canonicalUrl} />
      <link rel="alternate" hrefLang="zh-cn" href={metaData.canonicalUrl} />
      <link rel="alternate" hrefLang="zh-tw" href={metaData.canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={metaData.canonicalUrl} />
      
      {/* 网站验证和其他标签 */}
      <meta name="theme-color" content="#07284b" />
      <meta name="msapplication-TileColor" content="#07284b" />
      
      {/* JSON-LD结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      
      {/* 自定义meta标签 */}
      {customSeo.additionalMeta && customSeo.additionalMeta.map((meta, index) => (
        <meta key={index} {...meta} />
      ))}
      
      {/* 自定义link标签 */}
      {customSeo.additionalLinks && customSeo.additionalLinks.map((link, index) => (
        <link key={index} {...link} />
      ))}
    </Helmet>
  );
};

export default SEOHead;
